import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Sparkline } from "../components/charts";
import { EventStream } from "../components/EventStream";
import { api } from "../lib/api";
import { fmtAgo, fmtDateTime } from "../lib/format";
import { mergeEvents, useLive } from "../lib/ws";

const toNum = (v: unknown): number | null => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const m = v.match(/-?\d+(\.\d+)?/);
    return m ? Number(m[0]) : null;
  }
  if (typeof v === "boolean") return v ? 1 : 0;
  return null;
};

export function Feeds() {
  const { deviceId = "" } = useParams();
  const loops = useQuery({ queryKey: ["loops", deviceId], queryFn: () => api.loops(deviceId), refetchInterval: 10_000 });
  const feeds = useQuery({ queryKey: ["feeds", deviceId], queryFn: () => api.feeds(deviceId), refetchInterval: 15_000 });
  const live = useLive({ deviceId, kinds: ["loop.*", "feed.*", "capability.*", "harness.tick", "harness.sample", "harness.heal", "harness.quarantine"] }, { limit: 500 });
  const [sel, setSel] = useState<number | null>(null);

  const samples = useMemo(() => mergeEvents(feeds.data?.history ?? [], live.filter((e) => e.kind === "feed.sample" || e.kind === "harness.sample")), [feeds.data, live]);
  const series = useMemo(() => {
    const m = new Map<string, { ts: number; value: unknown }[]>();
    for (const e of samples) {
      const p = e.payload as any;
      if (!p?.label) continue;
      const arr = m.get(p.label) ?? [];
      arr.push({ ts: e.ts, value: p.value });
      m.set(p.label, arr);
    }
    return m;
  }, [samples]);

  const ticks = useMemo(() => live.filter((e) => (e.kind === "loop.tick" || e.kind === "harness.tick") && (sel == null || ((e.payload as any)?.loopId ?? (e.payload as any)?.id) === sel)), [live, sel]);
  const healing = useMemo(() => live.filter((e) => e.kind.startsWith("capability.") || e.kind === "harness.heal" || e.kind === "harness.quarantine"), [live]);

  return (
    <div className="stack">
      <h1>Loops & feeds</h1>
      <div className="panel">
        <h3>loops</h3>
        {(loops.data ?? []).length === 0 ? (
          <div className="empty">no loops recorded</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>id</th>
                <th>label</th>
                <th>capability</th>
                <th>ticks</th>
                <th>last tick</th>
                <th>installed</th>
                <th>killed</th>
              </tr>
            </thead>
            <tbody>
              {(loops.data ?? []).map((l) => (
                <tr key={l.loop_id} className={`clickable ${sel === l.loop_id ? "on" : ""}`} onClick={() => setSel(sel === l.loop_id ? null : l.loop_id)} style={sel === l.loop_id ? { background: "var(--panel-2)" } : undefined}>
                  <td className="mono">#{l.loop_id}</td>
                  <td>{l.label}</td>
                  <td className="mono">{l.capability_key ?? "—"}</td>
                  <td>{l.ticks}</td>
                  <td>
                    {l.last_ok === 0 ? <span className="badge fail">failed</span> : <span className="badge ok">ok</span>} <span className="muted">{fmtAgo(l.last_tick_ts)}</span>
                  </td>
                  <td className="muted">{fmtDateTime(l.installed_ts)}</td>
                  <td className="muted">{l.killed_ts ? fmtDateTime(l.killed_ts) : <span className="badge running">running</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="split">
        <div className="panel">
          <h3>tick history {sel != null && `· loop #${sel}`}</h3>
          <EventStream events={ticks} deviceId={deviceId} />
        </div>
        <div className="stack">
          <div className="panel">
            <h3>tiles</h3>
            {series.size === 0 ? (
              <div className="muted">no samples</div>
            ) : (
              <div className="tiles">
                {[...series.entries()].map(([label, pts]) => {
                  const last = pts[pts.length - 1];
                  const numeric = pts.map((p) => ({ ts: p.ts, value: toNum(p.value) }));
                  const misses = pts.filter((p) => p.value == null).length;
                  return (
                    <div key={label} className="tile">
                      <div className="label">{label}</div>
                      <div className={`value ${last.value == null ? "null" : ""}`}>{last.value == null ? "null" : String(last.value)}</div>
                      <Sparkline points={numeric.slice(-40)} />
                      <div className="muted" style={{ fontSize: 11 }}>
                        {pts.length} samples · {misses} missed · {fmtAgo(last.ts)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="panel">
            <h3>heal / quarantine / learned</h3>
            {healing.length === 0 ? <div className="muted">nothing yet</div> : <EventStream events={healing} deviceId={deviceId} />}
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            Every loop tick is a child run (parent <code>loop:&lt;id&gt;</code>); open a tick's run link to see what the model read that tick. <Link to={`/d/${deviceId}/events`}>raw events →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
